// src/components/common/audio-player/dsp/useAudioVisualizer.ts

import { useCallback, useEffect, useRef } from "react";

type Opts = {
  playing: boolean;
  rate: number;
  isMobile: boolean;
  reduceMotion: boolean;
};

export function useAudioVisualizer(
  audioRef: React.RefObject<HTMLAudioElement>,
  canvasRef: React.RefObject<HTMLCanvasElement>,
  opts: Opts
) {
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const srcNodeRef = useRef<
    MediaElementAudioSourceNode | MediaStreamAudioSourceNode | null
  >(null);

  const rafRef = useRef<number | null>(null);
  const roRef = useRef<ResizeObserver | null>(null);

  const visualReadyRef = useRef(false);
  const retryRafRef = useRef<number | null>(null);

  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width || 1, canvas.height || 1);
  }, [canvasRef]);

  const destroy = useCallback(() => {
    try {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    } catch {}
    rafRef.current = null;

    try {
      if (retryRafRef.current) cancelAnimationFrame(retryRafRef.current);
    } catch {}
    retryRafRef.current = null;

    try {
      roRef.current?.disconnect?.();
    } catch {}
    roRef.current = null;

    try {
      analyserRef.current?.disconnect?.();
    } catch {}
    analyserRef.current = null;

    try {
      srcNodeRef.current?.disconnect?.();
    } catch {}
    srcNodeRef.current = null;

    try {
      audioCtxRef.current?.close?.();
    } catch {}
    audioCtxRef.current = null;

    visualReadyRef.current = false;
    clearCanvas();
  }, [clearCanvas]);

  const ensure = useCallback(() => {
    // Keep mobile playback path simple and stable
    if (opts.isMobile || opts.reduceMotion) return;

    const el = audioRef.current;
    const canvas = canvasRef.current;
    if (!el || !canvas) return;

    const cw = canvas.clientWidth;
    const ch = canvas.clientHeight;

    if (!cw || !ch) {
      if (!retryRafRef.current) {
        retryRafRef.current = requestAnimationFrame(() => {
          retryRafRef.current = null;
          ensure();
        });
      }
      return;
    }

    const existingCtx = audioCtxRef.current;
    if (visualReadyRef.current && existingCtx) {
      if (existingCtx.state === "suspended") {
        existingCtx.resume().catch(() => {});
      }
      return;
    }

    try {
      const AudioCtx =
        (window as any).AudioContext || (window as any).webkitAudioContext;

      const audioCtx: AudioContext = existingCtx || new AudioCtx();
      if (audioCtx.state === "suspended") {
        audioCtx.resume().catch(() => {});
      }

      const analyser = analyserRef.current || audioCtx.createAnalyser();
      analyser.fftSize = 256;

      let srcNode = srcNodeRef.current;
      let usedCaptureStream = false;

      if (!srcNode) {
        const cap = (el as any).captureStream?.bind(el);
        if (cap) {
          try {
            const stream: MediaStream = cap();
            srcNode = audioCtx.createMediaStreamSource(stream);
            usedCaptureStream = true;
          } catch {
            // fallback below
          }
        }
      }

      if (!srcNode) {
        srcNode = audioCtx.createMediaElementSource(el);
      }

      try {
        srcNode.disconnect();
      } catch {}

      try {
        analyser.disconnect();
      } catch {}

      srcNode.connect(analyser);

      // Desktop only: route audio through WebAudio graph
      if (!usedCaptureStream) {
        analyser.connect(audioCtx.destination);
      } else {
        const gain = audioCtx.createGain();
        gain.gain.value = 0;
        analyser.connect(gain);
        gain.connect(audioCtx.destination);
      }

      audioCtxRef.current = audioCtx;
      analyserRef.current = analyser;
      srcNodeRef.current = srcNode;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const data = new Uint8Array(analyser.frequencyBinCount);

      const setCanvasSize = () => {
        const r = canvas.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        canvas.width = Math.max(1, Math.floor(r.width * dpr));
        canvas.height = Math.max(1, Math.floor(r.height * dpr));
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      };

      setCanvasSize();

      if (typeof ResizeObserver !== "undefined") {
        roRef.current?.disconnect?.();
        roRef.current = new ResizeObserver(setCanvasSize);
        roRef.current.observe(canvas);
      }

      const draw = () => {
        analyser.getByteFrequencyData(data);

        const w = canvas.clientWidth;
        const h = canvas.clientHeight;

        ctx.clearRect(0, 0, w, h);

        const bars = 28;
        const step = Math.max(1, Math.floor(data.length / bars));
        const barW = w / bars;

        for (let i = 0; i < bars; i++) {
          const v = data[i * step] / 255;
          const bh = Math.max(2, v * h);
          const x = i * barW + 1;
          const y = h - bh;
          ctx.fillStyle = "#0F52BA";
          ctx.fillRect(x, y, Math.max(1, barW - 2), bh);
        }

        rafRef.current = requestAnimationFrame(draw);
      };

      draw();
      visualReadyRef.current = true;
    } catch {
      visualReadyRef.current = false;

      if (!retryRafRef.current) {
        retryRafRef.current = requestAnimationFrame(() => {
          retryRafRef.current = null;
          ensure();
        });
      }
    }
  }, [audioRef, canvasRef, opts.isMobile, opts.reduceMotion]);

  useEffect(() => {
    if (opts.isMobile || opts.reduceMotion) {
      destroy();
      return;
    }

    if (opts.playing) ensure();
  }, [opts.isMobile, opts.reduceMotion, opts.playing, ensure, destroy]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el || opts.isMobile || opts.reduceMotion) return;

    const onPlay = () => ensure();
    el.addEventListener("play", onPlay);
    return () => el.removeEventListener("play", onPlay);
  }, [ensure, audioRef, opts.isMobile, opts.reduceMotion]);

  useEffect(() => destroy, [destroy]);

  return { destroy };
}